import { useLocation } from "react-router-dom";
import { ButtonV1 } from "../atoms/button";
import SearchBar from "../atoms/searchBar";
import { ChangeEvent, useRef, useState } from "react";
import { EditModal } from "../molecules/EditModal";
import snackbar from "../../api/snackbar/useSnackbar";
import {
  useBulkExportMutation,
  useBulkImportMutation,
} from "../../api/bulkUpload";
import { SelectInputBox } from "../atoms/selectInput";

const BULK_SAMPLE_PATH = "/Bulk_Upload_Format_Sample.xlsx";
const REQUIRED_BULK_FIELDS = [
  "ID",
  "Title",
  "New Title",
  "Region",
  "Country",
  "Brands",
  "Categories",
  "Ad Types",
  "Stage",
  "Year",
  "DT Levels",
  "File Name",
  "Language",
  "Media Type",
];
const ALLOWED_MEDIA_TYPES = ["Video", "Image", "PPT"];

interface props {
  qty: number;
  name: string;
  regex?: string;
  filterType?: string;
  sortType?: string;
  setAddNew?: Function;
  setSortType?: Function;
  setUpdateType?: Function;
  setFilterType?: Function;
  bulkUpdate?: Function;
  setRegex?: Function;
}

export default function AdminPageFilters({
  qty,
  name,
  setAddNew,
  setRegex,
  sortType,
  filterType,
  setSortType,
  setUpdateType,
  setFilterType,
  bulkUpdate,
  regex,
}: props) {
  const location = useLocation();
  const { pathname } = location;
  const [isImport, setIsImport] = useState(false);
  const [isExport, setIsExport] = useState(false);
  const [file, setFile] = useState<any>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showResults, setShowResults] = useState(false);
  const [uploadResults, setUploadResults] = useState<{
    acceptedCount: number;
    skippedCount: number;
    skippedRows: Array<{ rowNumber: number; errors: string[] }>;
    message: string;
    isError: boolean;
  } | null>(null);
  const [
    createImport,
    { isLoading: createImportIsLoading, isSuccess: createImportSuccess },
  ] = useBulkImportMutation({});

  const [
    createExport,
    { isLoading: createExportIsLoading, isSuccess: createExportSuccess },
  ] = useBulkExportMutation({});

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event?.target.files && event?.target?.files[0]);
  };

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Import`,
        category: "User Interaction",
        action: "Button Click",
        label: `Import Button`,
      });
    }
    e.preventDefault();

    // Prevent submission if already uploading
    if (createImportIsLoading) {
      return;
    }

    if (!file) {
      snackbar.error("Please choose a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("files", file);

    createImport(formData)
      .unwrap()
      .then((res) => {
        setIsImport(false);
        console.log("formSubmitted");
        const responseData = res?.data;
        const successMessage =
          res?.message ||
          (typeof responseData === "string"
            ? responseData
            : "Bulk upload completed");

        // Prepare results for dialog
        const acceptedCount = responseData?.acceptedCount || 0;
        const skippedCount = responseData?.skippedCount || 0;
        const skippedRows = Array.isArray(responseData?.skippedRows)
          ? responseData.skippedRows
          : [];

        setUploadResults({
          acceptedCount,
          skippedCount,
          skippedRows,
          message: successMessage,
          isError: false,
        });
        setShowResults(true);

        setFile(undefined);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      })
      .catch((error) => {
        setIsImport(false);
        console.log(error);
        
        // Check if error data contains rowErrors array (when all rows fail validation)
        const rowErrors = Array.isArray(error?.data?.data)
          ? error.data.data
          : [];

        // Prepare error results for dialog
        let skippedRows: Array<{ rowNumber: number; errors: string[] }> = [];
        
        if (rowErrors.length > 0) {
          skippedRows = rowErrors
            .map((row: any) => {
              if (row?.rowNumber && Array.isArray(row?.errors)) {
                return { rowNumber: row.rowNumber, errors: row.errors };
              }
              // Handle case where row might be a string or different format
              if (typeof row === "string") {
                return { rowNumber: 0, errors: [row] };
              }
              return { rowNumber: 0, errors: [JSON.stringify(row)] };
            })
            .filter((row: any) => row.rowNumber > 0 || row.errors.length > 0);
        }

        setUploadResults({
          acceptedCount: 0,
          skippedCount: skippedRows.length,
          skippedRows,
          message: error?.data?.message || "Bulk upload failed",
          isError: true,
        });
        setShowResults(true);
      });
  }

  function handleExport() {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Export`,
        category: "User Interaction",
        action: "Button Click",
        label: `Export Button`,
      });
    }
    createExport({
      published: filterType || "published",
    })
      .unwrap()
      .then(async (res) => {
        console.log("data", res.data);
        if (res.data) {
          const a = document.createElement("a"); // Create a temporary anchor element
          a.href = res.data; // Set the URL as the anchor's href attribute
          a.download = "export.xlsx"; // Set the filename for the downloaded file

          document.body.appendChild(a); // Append the anchor element to the document body
          a.click(); // Programmatically trigger a click event on the anchor element

          // Clean up resources
          window.URL.revokeObjectURL(res.data); // Revoke the URL object to free up memory
          document.body.removeChild(a); // Remove the anchor element from the document body

          snackbar.success("Exported successfully");
        } else {
          // Handle error if the response status is not 200
          console.error("Unexpected response status:", res.status);
          snackbar.error("Export failed: Unexpected response status");
        }
      })
      .catch((error) => {
        console.error("Export failed:", error);
        snackbar.error("Export failed");
      })
      .finally(() => {
        setIsExport(false);
      });
  }


  const filterItems = [];
  pathname === "/admin" &&
    filterItems.push(
      { value: "active", name: "Publish" },
      { value: "inActive", name: "Unpublish" },
      { value: "isDisabled", name: "Disabled" },
    );

  const title = pathname === "/admin" ? "newTitle" : "title";

  const sortItems = [
    { value: "createdAt", name: "last created" },
    { value: title, name: "title" },
  ];

  const updateItems = [{ value: "delete", name: "delete" }];
  pathname === "/admin" &&
    updateItems.push({ value: "publish", name: "Publish" });
  pathname === "/admin" &&
    updateItems.push({ value: "unpublish", name: "unpublish" });
  pathname === "/admin" &&
    updateItems.push({ value: "enable", name: "Enable" });
  pathname === "/admin" &&
    updateItems.push({ value: "disable", name: "Disable" });

  return (
    <div className="w-full whitespace-nowrap">
      <div className="flex sticky top-0 left-0 justify-between w-full pb-4 flex-wrap gap-y-2 ">
        <p className="text-[2rem] font-semibold flex flex-col capitalize">
          {name}
          <span className="text-sm text-left"> Total Count: {qty}</span>
        </p>
        <div className="flex gap-4">
          {(pathname === "/admin") && (
            <div className="flex items-center">
              <h1 className="mr-4">Filter :</h1>
              <div className="w-max mt-1 mr-2 px-4 py-2 border rounded-xl">
                <SelectInputBox
                  items={filterItems}
                  value={filterItems[0].value}
                  onChange={setFilterType}
                />
              </div>
            </div>
          )}
          <div className="flex items-center">
            <h1 className="mr-4">Sort :</h1>
            <div className="w-max mt-1 mr-2 px-4 py-2 border rounded-xl">
              <SelectInputBox
                items={sortItems}
                value={sortItems[0].value}
                onChange={setSortType}
              />
            </div>
          </div>
          <SearchBar setRegex={setRegex} regex={regex} />
        </div>
      </div>
      <div className="flex justify-between w-full">
        <div className="flex items-center mb-2">
          <div className="w-max mr-2 m-auto px-4 py-2 border rounded-xl">
            <SelectInputBox
              items={updateItems}
              value={""}
              onChange={setUpdateType}
            />
          </div>
          <div className="w-min">
            <ButtonV1 color="red" setActive={() => bulkUpdate && bulkUpdate()}>
              Go
            </ButtonV1>
          </div>
        </div>
        <div className="flex gap-4 w-min h-min">
          {pathname === "/admin" && (
            <ButtonV1 color="red" setActive={() => setIsExport(true)}>
              Export
            </ButtonV1>
          )}
          {pathname === "/admin" && (
            <ButtonV1 color="red" setActive={() => setIsImport(true)}>
              bulk Add
            </ButtonV1>
          )}
          {pathname !== "/admin/users" && (
            <ButtonV1 color="red" setActive={() => setAddNew && setAddNew()}>
              Add new
            </ButtonV1>
          )}
        </div>
        <EditModal
          open={isImport}
          handleClose={() => {
            if (!createImportIsLoading) {
              setIsImport(false);
            }
          }}
        >
          <form onSubmit={handleSubmit}>
            <div className="text-sm text-gray-700">
              <p className="mb-2">
                Need a template? Please feel free to{" "}
                <a
                  href={BULK_SAMPLE_PATH}
                  download
                  className="text-blue-600 underline font-semibold"
                >
                  download the sample file
                </a>
                . <br />
                To ensure a successful upload, kindly ensure that the columns
                marked with red-highlighted header columns are mandatory for
                processing.
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Media Type must be one of: {ALLOWED_MEDIA_TYPES.join(", ")}
              </p>
            </div> 
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => handleFileChange(e)}
              disabled={createImportIsLoading}
              className="my-4 w-full disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <ButtonV1
              color="blue"
              type="submit"
              className={
                createImportIsLoading
                  ? "opacity-50 cursor-not-allowed pointer-events-none"
                  : ""
              }
            >
              {createImportIsLoading ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Uploading...
                </span>
              ) : (
                "Upload"
              )}
            </ButtonV1>
          </form>
        </EditModal>
        <EditModal open={isExport} handleClose={() => setIsExport(false)}>
          <p className="w-full text-center text-lg font-semibold my-6">
            You are going to <br />
            export all the Creatives
          </p>
          <div className="flex gap-4 px-4">
            <ButtonV1 color="red" setActive={() => setIsExport(false)}>
              Cancel
            </ButtonV1>
            <ButtonV1 color="blue" setActive={handleExport}>
              Export
            </ButtonV1>
          </div>
        </EditModal>
        <EditModal
          open={showResults}
          handleClose={() => {
            setShowResults(false);
            setUploadResults(null);
          }}
        >
          <div className="w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              Bulk Upload Results
            </h2>

            {/* Summary Section */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex gap-6">
                <div>
                  <span className="text-sm text-gray-600">Accepted: </span>
                  <span
                    className={`text-lg font-semibold ${
                      (uploadResults?.acceptedCount ?? 0) > 0
                        ? "text-green-600"
                        : "text-gray-400"
                    }`}
                  >
                    {uploadResults?.acceptedCount ?? 0}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Skipped: </span>
                  <span
                    className={`text-lg font-semibold ${
                      (uploadResults?.skippedCount ?? 0) > 0
                        ? "text-red-600"
                        : "text-gray-400"
                    }`}
                  >
                    {uploadResults?.skippedCount ?? 0}
                  </span>
                </div>
              </div>
              <p
                className={`mt-2 text-sm ${
                  uploadResults?.isError ? "text-red-600" : "text-gray-700"
                }`}
              >
                {uploadResults?.message}
              </p>
            </div>

            {/* Error Table */}
            {uploadResults?.skippedRows &&
              uploadResults.skippedRows.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-md font-semibold mb-2 text-red-600">
                    Errors ({uploadResults.skippedRows.length} row(s))
                  </h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-red-50">
                        <tr>
                          <th className="px-4 py-2 text-left border-b font-semibold">
                            Row Number
                          </th>
                          <th className="px-4 py-2 text-left border-b font-semibold">
                            Errors
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResults.skippedRows.map((row, index) => (
                          <tr
                            key={index}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="px-4 py-2 font-medium">
                              {row.rowNumber}
                            </td>
                            <td className="px-4 py-2">
                              <ul className="list-disc list-inside">
                                {Array.isArray(row.errors) ? (
                                  row.errors.map((error, errIndex) => (
                                    <li key={errIndex} className="text-red-600">
                                      {error}
                                    </li>
                                  ))
                                ) : (
                                  <li className="text-red-600">
                                    {String(row.errors)}
                                  </li>
                                )}
                              </ul>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            {/* Success Message */}
            {uploadResults &&
              !uploadResults.isError &&
              uploadResults.acceptedCount > 0 && (
                <div className="mb-4 p-4 bg-green-50 rounded-lg">
                  <p className="text-green-700 font-medium">
                    ✓ Successfully uploaded {uploadResults.acceptedCount} row(s)
                  </p>
                </div>
              )}

            {/* Close Button */}
            <div className="flex justify-end mt-4">
              <ButtonV1
                color="blue"
                setActive={() => {
                  setShowResults(false);
                  setUploadResults(null);
                }}
              >
                Close
              </ButtonV1>
            </div>
          </div>
        </EditModal>
      </div>
    </div>
  );
}
